import React, { FunctionComponent, useState } from "react";
import SocialCardMedia from "./SocialCardMedia";
import SocialCardShareToolbar from "./SocialCardShareToolbar";
import {FiltersContext, searchHighlightRegExp} from "../../service/filters";
import { safeHtml } from "../../service/tools/safeHtml";

import {
    Avatar,
    Box,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    Tooltip,
    Typography,
    useTheme
} from "@material-ui/core";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";
import RepeatIcon from "@material-ui/icons/Repeat";
import ReplyIcon from "@material-ui/icons/Reply";
import { ShareButton, TranslateContentButton, ViewPostButton } from "../button";
import { SocialItem } from "../../service/data";
import { profileUrl } from "../../service/data/socialContent";
import { getSocialIcon } from "./socialUtils";
import { useSocialCardStyles } from "./useSocialCardStyles";
import { useTranslate } from "../../service/translation";
import classNames from "classnames";
import {apiProvider} from "../../service/data/apiProvider";
import { Link } from "react-router-dom";

interface SocialCardProps {
    record: SocialItem;
    /**
     * True when this card is one of several inside a SocialCardStack. It then drops the `masonryItem`
     * class: on the timeline the stack itself is the masonry item, and a nested one confuses the
     * layout into treating the cards inside a stack as siblings of it.
     */
    inStack?: boolean;
}

const SocialCard: FunctionComponent<SocialCardProps> = (props: SocialCardProps) => {
    const classes = useSocialCardStyles();
    const translate = useTranslate();
    const [toolbarCollapsed, setToolbarCollapsed] = useState(true);
    const { record, inStack } = props;
    const { content, mep, media, postedAt,timeAgo, source, postType, repostedFrom, inReplyTo, quoted } = record;
    const { avatar, country, firstName, lastName, politicalGroup, type} = mep;
    const [translation,setTranslation] = React.useState('');
    const [classesTranslate, setClassesTranslate] = useState('');
    const [classesShare, setClassesShare] = useState('');
    const [translateText,setTranslateText] = useState(translate("newshub.action.translateContent"));
    const [filters,setFilters] = React.useContext(FiltersContext);

    var componentTranslation:any = React.useRef(null);

    const regexp = React.useMemo(() => searchHighlightRegExp(filters.searchBar), [filters.searchBar]);

    React.useEffect( () => {
        moveToViewCompleted();
        componentTranslation.current.focus();
    },[translation]);

    const formatter = new Intl.DateTimeFormat('en-GB', {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
    });

    const handleToolbarToggle = () => {
        let colapsed = ! toolbarCollapsed;
        let newclas = colapsed? '' : classes.active;
        setToolbarCollapsed(colapsed);
        setClassesShare(newclas);

    };


    const moveToViewCompleted = () => {
        if(translation.length < 1) return;
        var cardBottom = componentTranslation.current.parentElement.parentElement.getBoundingClientRect().bottom;
        var offset = window.innerHeight - cardBottom;
        if(offset > 0 ) return;
        document.documentElement.scrollTop -= offset;
    };


    const handleToolbarToggleKeyboard = () => setToolbarCollapsed(false);
    const handleTranslate = () => {
        if(translation.length === 0){
            apiProvider.getTranslation('' + record.id)
            .then(response => {
                setTranslation(response.content);
                setClassesTranslate(classes.active);
                setTranslateText(translate("newshub.action.hideTranslation"));

            }).catch(e => {});
        }else {
            setTranslateText(translate("newshub.action.translateContent"));
            setTranslation('');
            setClassesTranslate('');

        }
    };

    const highlightContent = (text:string):string => {
        if(regexp === null) return text;
        let a1:string[] = text.split('<');
        for(let i  in a1 ){
            let a2:string[] = a1[i].split('>');
            a2 = a2.map((currentValue:string,index:number):string => {
                if(index === 0) return currentValue;
                return currentValue.replace(regexp, (item:string,i:number) => {
                    return '<mark>'+item+'</mark>';
                 });
            });
            a1[i] = a2.join('>');
        }
        return a1.join('<');
    };




    /**
     * A repost is filed under the account that reposted it, so without this the card reads as if they
     * wrote it. Same treatment for both networks: the API reports post type in terms that do not name
     * either one.
     *
     * Its own line directly above the post, aligned with it, so it qualifies what follows. It used to
     * be appended to a "Tweeted" line in the header, where it read as a fact about the author and
     * repeated a network already named by the mark beside the time and by the "View on X" button.
     *
     * Nothing at all for an original post. In a feed of posts, "Tweeted" is not news.
     */
    const renderPostType = () => {
        if (! postType || postType === "original") {
            return null;
        }
        const icon = postType === "repost" ? <RepeatIcon className={classes.postTypeIcon} />
            : postType === "quote" ? <FormatQuoteIcon className={classes.postTypeIcon} />
            : <ReplyIcon className={classes.postTypeIcon} />;

        // A quote the API could attribute is shown the way X shows it: the quoted post as a box
        // under the text (renderQuotedPost). Nothing above the text then - the box is the signal.
        if (postType === "quote" && quoted) {
            return null;
        }

        // The other account, when the API knows it: who was reposted, who is being replied to. An
        // item stored before the ingest recorded it has none, and neither has a Bluesky quote yet,
        // which is why each falls back to a bare phrase.
        const handle = postType === "repost" ? repostedFrom
            : postType === "reply" ? inReplyTo
            : undefined;
        const profile = handle ? profileUrl(source, handle) : undefined;

        return (
            <Typography variant="subtitle2" component="p" className={classes.postType}>
                {icon}
                <span>
                    {handle
                        ? <>
                            {translate("social.message." + (postType === "repost" ? "repostedFrom" : "inReplyTo"))}{" "}
                            {profile
                                ? <a href={profile} target="_blank" rel="noopener noreferrer">@{handle}</a>
                                : <>@{handle}</>}
                        </>
                        : translate("social.message." + postType)}
                </span>
            </Typography>
        );
    };

    /**
     * The quoted post, as a box under the quote's own words - the shape X gives it, which is what a
     * reader expects a quote to look like: the quoted account's avatar, name and handle, then its
     * words, then where the link goes. The whole box is the link. An item stored before the ingest
     * kept the quoted post has the handle and the address only, and the box says that much. Not
     * rendered when the API could not attribute the quote at all.
     *
     * The quoted text is rendered as text, not HTML: it is another account's words, and nothing
     * here needs it linkified.
     */
    const renderQuotedPost = () => {
        if (! quoted) {
            return null;
        }
        const network = translate("social.network." + source);
        return (
            <a
                className={classes.quotedPost}
                href={quoted.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={translate("social.message.viewQuotedPost", { network })}
            >
                <span className={classes.quotedPostHeader}>
                    {quoted.avatarUrl
                        ? <Avatar className={classes.quotedPostAvatar} src={quoted.avatarUrl} alt="" />
                        : <FormatQuoteIcon className={classes.postTypeIcon} />}
                    {quoted.displayName && <span className={classes.quotedPostAuthor}>{quoted.displayName}</span>}
                    <span className={quoted.displayName ? classes.quotedPostLabel : classes.quotedPostAuthor}>@{quoted.handle}</span>
                </span>
                {quoted.text && <span className={classes.quotedPostText}>{quoted.text}</span>}
                <span className={classes.quotedPostLink}>
                    {translate("social.message.viewQuotedPost", { network })}
                </span>
            </a>
        );
    };

    /**
     * "EPP · Finland". The abbreviation rather than the full party name, which runs to sixty
     * characters and, set bold at 14px as it was, shouted as loudly as the person who posted.
     * The full name stays available as the link's title.
     */
    const renderAffiliation = () => {
        // A post's type is the *feeder's* authorType, not the contributor's, so this relabels only
        // posts from the presidency account. The same person's personal account keeps its political
        // group, which is the distinction that matters: she posts there as an MEP, not as President.
        if (type === "president") {
            return (
                <Typography variant="subtitle2" component="p" className={classes.affiliation}>
                    {translate("social.message.presidentTitle")}
                </Typography>
            );
        }

        const short = politicalGroup.eparty || politicalGroup.party;
        const parts = [short, country].filter(Boolean);
        if (parts.length === 0) {
            return null;
        }

        // Only linked when the group resolves to a contributor. It is looked up by code against the
        // group press accounts, and a code the lookup does not know gives 0 - a link to
        // /contributors/0 is a dead end, so the text stands on its own instead.
        return (
            <Typography variant="subtitle2" component="p" className={classes.affiliation}>
                {short && politicalGroup.id
                    ? <Link to={`/contributors/${politicalGroup.id}`} title={politicalGroup.party}>{short}</Link>
                    : short}
                {short && country ? " · " : ""}
                {country}
            </Typography>
        );
    };

    return (
        <Card className={classNames(classes.root, { masonryItem: ! inStack })} aria-live="polite">
            <Box className={classes.header}>
                <Box className={classes.avatar}>
                    <Link to={`/contributors/${mep.id}`} style={{ textDecoration: 'none'}}>

                        <Avatar className={classes.avatarImage + (mep.type === "mep" ? " codict-avatar" : " not-mep") } src={avatar} alt={firstName + ' ' + lastName}>
                            {firstName.charAt(0).toUpperCase()}
                            {lastName.charAt(0).toUpperCase()}
                        </Avatar>
                    </Link>
                </Box>
                <Box className={classes.content}>
                    <Box className={classes.nameLine}>
                        <Link to={`/contributors/${mep.id}`} style={{ textDecoration: 'none', minWidth: 0}}>
                            <Typography variant="h2" component="p" className={classes.name}>
                                <span className={classes.cardText} dangerouslySetInnerHTML={{ __html: safeHtml(highlightContent(`${firstName} ${lastName}`)) }} />
                            </Typography>
                        </Link>
                        <Box className={classes.source}>
                            <Tooltip title={formatter.format(postedAt)}>
                                <Typography variant="subtitle2" component="p">
                                    {timeAgo}
                                </Typography>
                            </Tooltip>
                            <span
                                className={classes.sourceIcon}
                                title={translate("social.network." + source)}
                            >
                                {getSocialIcon(source)}
                            </span>
                        </Box>
                    </Box>
                    {renderAffiliation()}
                </Box>
            </Box>
            <Box className={classes.stem}>
                {renderPostType()}
                <CardContent className={classes.contentContainer}>
                    <Typography variant="body1" component="div">
                        <div className={classNames(classes.cardText, classes.postText)} dangerouslySetInnerHTML={{ __html: safeHtml(highlightContent(content)) }} />
                    </Typography>
                    {renderQuotedPost()}
                </CardContent>
                <Box className={classes.mediaContainer}>
                    <SocialCardMedia
                        className={classes.media}
                        classes={classes}
                        media={media}
                        title={translate("social.message.postedBy", { ...mep })}
                        source={record.sourceUrl}
                        // Don't clip on the single-post view. EmbedContent renders this same list and is
                        // told apart by the filter it sets rather than by matching the URL - `/tw/` is
                        // already hardcoded in six places and does not need a seventh.
                        cap={! filters.form.itemId}
                    />
                </Box>
                <Typography variant="body1" component="div">
                    <div
                        ref={node => componentTranslation.current = node}
                        className={classes.cardTextTranslation}
                        dangerouslySetInnerHTML={{ __html: safeHtml(translation) }}
                    />
                </Typography>
                <CardActions className={classes.cardActions}>
                    <TranslateContentButton
                        className={classesTranslate}
                        onClick={handleTranslate}
                        aria-label={translateText}
                    />
                    <ViewPostButton
                        network={source}
                        href={record.sourceUrl}
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                    />

                    <ShareButton
                        className={classesShare}
                        onClick={handleToolbarToggle}
                        onKeyDown={handleToolbarToggleKeyboard}
                    />
                </CardActions>
            </Box>
            <CardMedia className={classes.cardMedia}>
                <SocialCardShareToolbar
                    classes={classes}
                    collapsed={toolbarCollapsed}
                    record={record}
                />
            </CardMedia>
        </Card>
    );
};

export default SocialCard;
